import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';

const sectionLabel = {
	color: THEME.colors.text.light,
	fontSize: THEME.typography.fontSizes.xs,
	fontWeight: THEME.typography.fontWeights.bold,
	letterSpacing: '0.04em',
	textTransform: 'uppercase',
} as const;

export const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: SPACE[16],
		minHeight: 0,
		minWidth: 0,
		// A full-width InputGroup's end-addon margin hangs 7px past its field, which would scroll this sideways on phones.
		overflowX: 'hidden',
		overflowY: 'auto',
	},
	controls: {
		alignItems: 'flex-end',
		display: 'flex',
		flexShrink: 0,
		flexWrap: 'wrap',
		gap: SPACE[16],
	},
	// Field defaults to full width; in the filter row each control sizes to its own trigger so the row wraps.
	controlField: {
		flex: '0 0 auto',
		width: 'auto',
		[THEME.queries.sm]: {
			flex: '1 1 100%',
		},
	},
	// The results popup takes this width, and narrower cuts off reps' emails.
	userSearchTrigger: {
		minWidth: 280,
		[THEME.queries.sm]: {
			minWidth: 0,
		},
	},
	groupTrigger: {
		minWidth: 150,
	},
	rangeError: {
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.sm,
	},
	dashboard: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: SPACE[12],
	},
	tileSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	tileSectionTitle: {
		...sectionLabel,
		margin: 0,
	},
	tileGrid: {
		display: 'grid',
		gap: SPACE[12],
		gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
	},
	tile: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderLeftWidth: 2,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		minWidth: 0,
		padding: `${SPACE[12]}px ${SPACE[16]}px`,
	},
	tileGood: {
		borderLeftColor: THEME.colors.success.main,
	},
	tileWarn: {
		borderLeftColor: THEME.colors.warning.main,
	},
	tileBad: {
		borderLeftColor: THEME.colors.danger.main,
	},
	tileNeutral: {
		borderLeftColor: THEME.colors.pill.gray.border,
	},
	tileLabel: {
		...sectionLabel,
	},
	tileValue: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: 1.2,
	},
	tileDetail: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
	autoTag: {
		display: 'inline-flex',
	},
	headlineNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		margin: 0,
	},
	state: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		padding: `${SPACE[24]}px 0`,
		textAlign: 'center',
	},
	tableWrap: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		flexShrink: 0,
		overflowX: 'auto',
	},
	table: {
		borderCollapse: 'collapse',
		fontSize: THEME.typography.fontSizes.sm,
		width: '100%',
	},
	th: {
		...sectionLabel,
		backgroundColor: THEME.colors.background.main,
		borderBottom: THEME.common.border,
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
		textAlign: 'right',
		whiteSpace: 'nowrap',
	},
	td: {
		borderBottom: THEME.common.border,
		color: THEME.colors.text.dark,
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
		textAlign: 'right',
		verticalAlign: 'middle',
		whiteSpace: 'nowrap',
	},
	alignLeft: {
		textAlign: 'left',
	},
	// The rep column wraps its tags under the name so it doesn't widen the table into a sideways scroll.
	repTd: {
		whiteSpace: 'normal',
	},
	repCell: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: `${SPACE[4]}px ${SPACE[8]}px`,
	},
	repToggle: {
		fontWeight: THEME.typography.fontWeights.bold,
		justifyContent: 'flex-start',
	},
	repCohort: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	neverFlag: {
		color: THEME.colors.danger.dark,
		fontSize: THEME.typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	pillCell: {
		display: 'inline-flex',
	},
	faded: {
		opacity: 0.5,
	},
	expandedCell: {
		backgroundColor: THEME.colors.background.main,
		borderBottom: THEME.common.border,
		padding: SPACE[12],
	},
	callList: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	listReset: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},
	call: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
	},
	callRow: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[12],
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
	},
	callMeta: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	callOutcome: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	callActions: {
		display: 'flex',
		gap: SPACE[8],
		marginLeft: 'auto',
	},
	threads: {
		borderTop: THEME.common.border,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[12],
		padding: SPACE[12],
	},
	threadTable: {
		borderCollapse: 'collapse',
		fontSize: THEME.typography.fontSizes.sm,
		width: '100%',
	},
	threadCell: {
		borderBottom: THEME.common.border,
		color: THEME.colors.text.dark,
		padding: `${SPACE[8]}px ${SPACE[8]}px`,
		textAlign: 'left',
		verticalAlign: 'top',
		whiteSpace: 'normal',
	},
	threadHeader: {
		...sectionLabel,
		borderBottom: THEME.common.border,
		padding: `${SPACE[4]}px ${SPACE[8]}px`,
		textAlign: 'left',
	},
	judgmentNote: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
	},
	judgmentNoteLabel: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	loadMore: {
		alignSelf: 'center',
	},
});
